import { Container, type Texture } from "pixi.js";
import { fitWithin } from "@slot/presentation";
import type { ContentAreaStore, DesignRect, FitRule, Log, PresentationTicker, ViewportSnapshot, ViewportStore, WinAmountLabelFactory } from "@slot/presentation";
import { PORTRAIT_CONTROLS_DROP_MAX, PORTRAIT_PURCHASE_DROP_MAX, portraitBarDrops, portraitControlsTop, type ControlBarLayout, type GameLogoSpec } from "@slot/game-shell";
import type { GameVisualAssets } from "../assets/GameVisualAssets";
import { SymbolCatalog } from "../assets/SymbolCatalog";
import { SECRET_BONUS_SYMBOL_ID } from "../assets/mergeLadder";
import { createWinAmountLabelFactory } from "../composition/createWinAmountLabel";
import { CHEF_GEOMETRY, ChefView, placeChef } from "../features/chef/ChefView";
import { CHEF_VIEW_TUNING } from "../tuning/chefTuning";
import { GRID_GEOMETRY, GridContainer } from "./GridContainer";
import { LADDER_GEOMETRY, LadderBarView } from "./LadderBarView";

/**
 * The symbols' art scale: the sheet draws them on a ~490 px cell (the FS packet, the largest, is 488 px); 0.33 draws
 * them as the layout example does on its 146-unit tiles (the FS packet's body 142 units, the pink candy's 128) and
 * keeps their drawn proportions.
 */
const SYMBOL_ART_SCALE = 0.33;

/**
 * How far each symbol sits below its cell's centre. Each texture carries its shadow under the candy, so the candy is
 * drawn 15–21 px of the sheet above the texture's centre (5–7 units at 0.33); these put each candy where the layout
 * example has it, measured there per symbol (the candies' centres 0–3 units above the cells', the heart's 1 below;
 * the Secret Bonus disc, drawn centred in its art, 1 above).
 */
const SYMBOL_OFFSET_Y: Readonly<Record<string, number>> = { L1: 5, L2: 6, L3: 6, L4: 8, H1: 3, H2: 2.5, H3: 4.5, H4: 3.5, FS: 4, [SECRET_BONUS_SYMBOL_ID]: -1 };

/** The composition: the ladder bar on top of the frame, as wide as the frame, from the bar's top down to the frame's bottom. */
const COMPOSITION = {
  width: GRID_GEOMETRY.frame,
  top: LADDER_GEOMETRY.y - LADDER_GEOMETRY.height / 2,
  bottom: GRID_GEOMETRY.frameOffsetY + GRID_GEOMETRY.frame / 2,
} as const;

/**
 * The logo's size: the art's 1.217:1 at 366 wide, so the logo itself (the art less its 50 px margins and the shadow under
 * it) is the layout example's 293 × 236 units on its phone, at the composition's 0.879.
 */
const LOGO = { width: 366, height: 301 } as const;

/**
 * How the composition fits each shape, the grid's centre at the root's origin. The control bar overlays the scene in
 * portrait and on desktop, so the box keeps its height free under the frame and scales with it: the frame clears the bar
 * at any window size. Portrait follows the layout example (`Supporting Files/game layout example.png`, a phone 914.5
 * units wide): the box's width fits that phone at 0.879, its centre 83.5 units below the cells' centre puts the cells'
 * centre 73 units above the screen's (the frame's rim from 440 to 1321 units down, as drawn there), and its height,
 * 2178, holds a 9:16 phone at the same scale, so the logo at the top left stays clear of the header there too.
 * Desktop and landscape put the logo beside the grid, the box symmetric about the grid's column so the grid stays
 * centred. Landscape fits the content area between the bars. A portrait screen wider than the box at that scale has the
 * bar's controls lower (`BAR_LAYOUT`), and the composition follows them down (`fitComposition`).
 */
export const COMPOSITION_ROOT = {
  width: COMPOSITION.width,
  height: COMPOSITION.bottom - COMPOSITION.top,
  fit: {
    box: {
      desktop: { width: 1840, height: 1516, centerY: 42 },
      mobile: { width: 1040, height: 2178, centerY: 83.5 },
      mobile_landscape: { width: 1840, height: 1236, centerY: -58 },
    },
    limits: { desktop: { maxWidth: 2400 }, mobile: { maxWidth: 1080 }, mobile_landscape: {} },
    offsetY: { desktop: 0, mobile: 0, mobile_landscape: 0 },
  },
} as const satisfies { width: number; height: number; fit: FitRule };

/** The fit-height canvas's height, in design units: a portrait screen's, whatever its width. */
const DESIGN_HEIGHT = 1920;

/** The shell's portrait box, which the bar's arrangement and the composition's portrait fit are measured from. */
const PORTRAIT_BOX = COMPOSITION_ROOT.fit.box.mobile;

/**
 * The bar on the portrait screens wider than the shell's box fits at their height (decision 78): from that width (916.8,
 * where the composition stops filling the width and leaves the sides empty) the controls go down as far as the bar's
 * geometry allows, the numbers' row staying, and the Buy Bonus button further, to 10 over the menu toggle (the shell
 * moves it only where it then clears the bet's minus button: from about 1015 wide). The frame follows them down, so the
 * grid grows into the sides.
 */
export const BAR_LAYOUT: ControlBarLayout = {
  mobile: [{ minWidth: (PORTRAIT_BOX.width * DESIGN_HEIGHT) / PORTRAIT_BOX.height, controlsDrop: PORTRAIT_CONTROLS_DROP_MAX, purchaseDrop: PORTRAIT_PURCHASE_DROP_MAX - 10 }],
};

/** The composition's height in portrait: from the chef's top (his artboard's, the highest art) to the frame's bottom. */
const PORTRAIT_EXTENT = { top: CHEF_GEOMETRY.portrait.y, bottom: COMPOSITION.bottom } as const;

/** How high the bar's controls reach in the shell's places, which `PORTRAIT_BOX` keeps the frame clear of. */
const SHELL_CONTROLS_TOP = portraitControlsTop({ controls: 0, purchase: 0 });

/**
 * The composition root's fit: the shell's box per shape, except in portrait with the bar's controls lower than the
 * shell's places (`BAR_LAYOUT`). There the frame may reach as much lower, so the composition grows until it fills the
 * screen's width or reaches that line, whichever comes first, its top (the chef's) where the shell's box puts it at the
 * screen's height, as on the layout example's phone: only downward, so it carries on from the shell's box at the width
 * where the arrangement starts.
 */
export function fitComposition(viewport: ViewportSnapshot, area: DesignRect): { scale: number; x: number; y: number } {
  const lowered = SHELL_CONTROLS_TOP - portraitControlsTop(portraitBarDrops(BAR_LAYOUT, viewport));
  if (lowered <= 0) return fitWithin(COMPOSITION_ROOT.fit, viewport.shape, area);
  // The shell's box at the screen's height: where it puts the chef's top and the frame's bottom, under the area's top.
  const fitted = (area.bottom - area.top) / PORTRAIT_BOX.height;
  const boxTop = PORTRAIT_BOX.centerY - PORTRAIT_BOX.height / 2;
  const top = (PORTRAIT_EXTENT.top - boxTop) * fitted;
  const frameBottom = (PORTRAIT_EXTENT.bottom - boxTop) * fitted + lowered;
  const byWidth = Math.min(COMPOSITION_ROOT.fit.limits.mobile.maxWidth, area.right - area.left) / PORTRAIT_BOX.width;
  const scale = Math.min(byWidth, (frameBottom - top) / (PORTRAIT_EXTENT.bottom - PORTRAIT_EXTENT.top));
  return { scale, x: (area.left + area.right) / 2, y: area.top + top - PORTRAIT_EXTENT.top * scale };
}

/**
 * The logo: in portrait at the top left over the ladder bar, as the layout example places it (the logo itself centred 205
 * units in from the left edge and 219 down on that phone, 10 above the art's centre); left of the grid's top on the wide
 * shapes.
 */
export const GAME_LOGO: GameLogoSpec = {
  box: LOGO,
  sprite: LOGO,
  placement: {
    desktop: { x: -730, y: -420 },
    mobile: { x: -287, y: -749 },
    mobile_landscape: { x: -730, y: -420 },
  },
};

/**
 * The scene: the composition root fitted per shape — the chef behind the ladder bar, the bar, the grid, and the overlay
 * the win labels float in (the background is under the canvas, `GameBackground`). The shell adds the logo as the root's
 * first child and its loading overlay as its last.
 */
export class GameScene extends Container {
  public readonly compositionRoot: Container;
  public readonly gridContainer: GridContainer;
  public readonly ladderBar: LadderBarView;
  public readonly chef: ChefView;
  /** Over the grid, in composition-root space: the win-amount labels. */
  public readonly fxOverlays: Container;
  public readonly symbolCatalog: SymbolCatalog;
  public readonly createWinAmountLabel: WinAmountLabelFactory;
  /** What each cell shows, from the grid the round presentation owns (it sets this); the dev hook reads it. */
  public boardReadout: (() => string[][]) | null = null;
  private readonly disposers: Array<() => void> = [];

  public constructor(input: { assets: GameVisualAssets; viewport: ViewportStore; contentArea: ContentAreaStore; ticker: PresentationTicker; log: Log }) {
    super({ label: "GameScene" });
    const { assets } = input;


    this.compositionRoot = new Container({ label: "GameCompositionRoot" });
    this.addChild(this.compositionRoot);

    this.chef = new ChefView({ url: assets.chef, ticker: input.ticker, tuning: CHEF_VIEW_TUNING, log: input.log });
    this.ladderBar = new LadderBarView({ bar: assets.textures.ladderBar, icons: assets.ladderIcons });
    this.gridContainer = new GridContainer({ frame: assets.textures.gridFrame, tile: assets.textures.cellTile });
    this.fxOverlays = new Container({ label: "FxOverlays" });
    this.compositionRoot.addChild(this.chef, this.ladderBar, this.gridContainer, this.fxOverlays);

    this.disposers.push(
      input.contentArea.subscribe((area) => {
        const viewport = input.viewport.getSnapshot();
        const { scale, x, y } = fitComposition(viewport, area);
        this.compositionRoot.scale.set(scale, scale);
        this.compositionRoot.position.set(x, y);
        // The chef where the shape stands him, his canvas at his size on the screen: units to CSS pixels to device pixels,
        // the app's resolution rule (the device's ratio, at most 2).
        const chef = placeChef({ shape: viewport.shape, design: viewport.design, area, root: { x, y, scale }, frameHalfWidth: GRID_GEOMETRY.frame / 2 });
        this.chef.layout(chef, scale * viewport.scale * Math.min(window.devicePixelRatio || 1, 2));
      }),
    );

    // The ladder's nine and the Secret Bonus disc (decision 51), drawn at the same scale.
    const symbols = new Map<string, Texture>(assets.symbols);
    if (assets.secretBonus.symbol) symbols.set(SECRET_BONUS_SYMBOL_ID, assets.secretBonus.symbol);
    this.symbolCatalog = new SymbolCatalog({ textures: symbols, artScale: SYMBOL_ART_SCALE, offsetsY: SYMBOL_OFFSET_Y });
    this.createWinAmountLabel = createWinAmountLabelFactory();
  }

  /** The board as the cells show it, rows top to bottom ("" for an empty cell); empty before the presentation exists. */
  public readBoard(): string[][] {
    return this.boardReadout?.() ?? [];
  }

  public dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.chef.dispose();
    this.gridContainer.dispose();
    this.destroy({ children: true });
  }
}
